import { _ as _extends } from '../../_rollupPluginBabelHelpers-c245b26a.js';
import React, { forwardRef, useState, useRef, useEffect, useCallback } from 'react';
import 'focus-visible';
import useId from '@indeed/ihl-use-id';
import { jsx } from '@emotion/react';
import { ButtonGroup } from '../ButtonGroup/ButtonGroup.js';
import { BUTTON_SIZES } from '../Button/Button.js';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden.js';
import '@indeed/ifl-css';
import '../../utils/displayName.js';
import '../../utils/typeGaurds.js';
import '../ButtonLink/ButtonLink.js';
import '../Button/context.js';
import '../LinkProvider/LinkProvider.js';
import '../IconWrapper/IconWrapper.js';
import '../../utils/transition.js';
import '../../utils/styled.js';
import '../../constants/index.js';
import '../../utils/createResponsive.js';
import '../../utils/transparentize.js';
import '../Spinner/Spinner.js';
import '../../utils/mergeRefs.js';
import '../../hooks/useResponsiveValue.js';
import '../../hooks/useBreakpointIndex.js';
import '../IconButton/IconButton.js';
import '../../utils/warnDeprecation.js';
import '../../utils/warn.js';
import '../DisclosurePopup/DisclosurePopupTrigger.js';
import '../DisclosurePopup/context.js';
import '../../hooks/useMergeRefs.js';

const InputHidden = VisuallyHidden.withComponent("input", process.env.NODE_ENV === "production" ? {
  target: "e1vn9odm0"
} : {
  target: "e1vn9odm0",
  label: "InputHidden"
});
/**
 * @see https://experience.indeed.design/components/ids/radio-button-group
 */
const RadioButtonGroup = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    name,
    disabled,
    defaultValue,
    legend = "",
    value,
    orientation = "auto",
    onChange,
    children,
    sx,
    ...props
  } = _ref;
  const [options, setOptions] = useState(undefined);
  const [selected, setSelected] = useState(value || defaultValue);
  const defaultId = `ifl-RadioButtonGroup-${useId()}`;
  const uid = props.id || defaultId;
  const hasMountedRef = useRef(false);
  useEffect(() => {
    if (hasMountedRef.current) {
      setSelected(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  useEffect(() => {
    hasMountedRef.current = true;
    return () => {
      hasMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleOnChange = e => {
    if (disabled) {
      return;
    }
    if (ref && "current" in ref) {
      ref.current = e.target;
    }
    if (onChange) {
      onChange(e);
    }
    if (e.target.value !== undefined) {
      setSelected(e.target.value);
    }
  };
  const filteredChildren = React.Children.map(children, child => ! /*#__PURE__*/React.isValidElement(child) ? null : child);
  const ids = filteredChildren.map((child, index) => child.props.id || `${uid}-${index}`);
  const handleOnFocus = useCallback(e => {
    // Runs ID-based lookup of React child to avoid having to define a React Hook in a loop
    const index = ids.findIndex(id => `${id}-radio` === e.target.id);
    if (index !== -1) {
      var _filteredChildren$ind;
      (_filteredChildren$ind = filteredChildren[index]) == null || (_filteredChildren$ind = _filteredChildren$ind.props) == null || _filteredChildren$ind.onFocus == null || _filteredChildren$ind.onFocus(e);
    }
    if (e.target.dataset.focusVisibleAdded !== undefined) {
      e.currentTarget.dataset.focusVisibleAdded = "";
    }
  }, [filteredChildren, ids]);
  const handleOnBlur = useCallback(e => {
    // Runs ID-based lookup of React child to avoid having to define a React Hook in a loop
    const index = ids.findIndex(id => `${id}-radio` === e.target.id);
    if (index !== -1) {
      var _filteredChildren$ind2;
      (_filteredChildren$ind2 = filteredChildren[index]) == null || (_filteredChildren$ind2 = _filteredChildren$ind2.props) == null || _filteredChildren$ind2.onBlur == null || _filteredChildren$ind2.onBlur(e);
    }
    delete e.currentTarget.dataset.focusVisibleAdded;
  }, [filteredChildren, ids]);
  const buttons = filteredChildren.map((child, index) => {
    const id = ids[index];
    const htmlFor = `${id}-radio`;
    const buttonValue = child.props.value || child.props.children;
    const isSelected = buttonValue === selected;
    const variant = isSelected ? "primary" : "secondary";
    const grandChildren = Array.isArray(child.props.children) ? child.props.children : [child.props.children];
    return /*#__PURE__*/React.cloneElement(child, {
      as: "label",
      id,
      value: undefined,
      htmlFor,
      disabled: disabled || child.props.disabled,
      variant,
      onFocus: handleOnFocus,
      onBlur: handleOnBlur,
      "data-value": buttonValue,
      "data-is-selected": isSelected ? "" : null,
      "data-aria-label": child.props["aria-label"],
      "aria-label": undefined,
      children: [options == null ? void 0 : options[index], ...grandChildren],
      sx: {
        // Ensure selected item is on top, needs to be higher than the :hover z-index set in ButtonGroup.
        // Needs !important because specificity is higher in ButtonGroup and there is no way to overcome here.
        zIndex: isSelected ? "3 !important" : "inherit",
        "&[data-focus-visible-added]": {
          zIndex: 1,
          boxShadow: "focus"
        },
        ...child.props.sx
      }
    });
  });
  useEffect(() => {
    setOptions(buttons.map(child => jsx(InputHidden, {
      type: "radio",
      name: name,
      disabled: disabled || child.props.disabled,
      id: child.props.htmlFor,
      key: child.props.htmlFor,
      value: child.props["data-value"],
      onChange: handleOnChange,
      checked: child.props["data-value"] === selected,
      "aria-label": child.props["data-aria-label"],
      ref: el => {
        if (ref && "current" in ref && el && el.value === selected) {
          ref.current = el;
        }
      }
    })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, uid, children, disabled]);
  return jsx(ButtonGroup, _extends({
    as: "fieldset",
    sx: {
      display: "flex",
      border: "0",
      paddingInlineStart: 0,
      paddingInlineEnd: 0,
      paddingBlockStart: 0,
      paddingBlockEnd: 0,
      marginInlineStart: 0,
      marginInlineEnd: 0,
      marginBlockStart: 0,
      marginBlockEnd: 0,
      minInlineSize: "0",
      ...sx
    }
    // update to "auto" in IFL 5.0
    ,
    orientation: orientation,
    disabled: disabled,
    variant: null
  }, props), legend && jsx(VisuallyHidden, {
    as: "legend",
    key: "legend"
  }, legend), buttons);
});
const RADIO_BUTTON_GROUP_SIZES = BUTTON_SIZES;
RadioButtonGroup.displayName = "RadioButtonGroup";

export { RADIO_BUTTON_GROUP_SIZES, RadioButtonGroup, RadioButtonGroup as default };
